import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Package, Users, ShoppingCart, DollarSign,
  Truck, Boxes, Building2, UserCog, LogOut, Menu, X
} from 'lucide-react';

const LOGO_URL = '/images/logo.png';

const allMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
  { icon: Package, label: 'Products', path: '/admin/products' },
  { icon: Users, label: 'Customers', path: '/admin/customers' },
  { icon: ShoppingCart, label: 'Sales', path: '/admin/sales' },
  { icon: DollarSign, label: 'Expenses', path: '/admin/expenses' },
  { icon: Boxes, label: 'Inventory', path: '/admin/inventory' },
];

const superAdminItems = [
  { icon: Building2, label: 'Franchises', path: '/admin/franchises' },
  { icon: UserCog, label: 'Users', path: '/admin/users' },
];

export default function AdminLayout({ children, activeItem }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const menuItems = user?.role === 'super_admin'
    ? [...allMenuItems, ...superAdminItems]
    : allMenuItems;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-layout">
      <aside
        className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-brand-primary text-white transition-all duration-300 flex flex-col flex-shrink-0`}
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            {sidebarOpen && <img src={LOGO_URL} alt="ZAN Aqua" className="h-12 brightness-0 invert" />}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:text-brand-accent transition-colors duration-200"
              data-testid="sidebar-toggle-button"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        <nav className="flex-1 py-6" data-testid="admin-nav">
          {menuItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className={`flex items-center px-6 py-3 transition-all duration-200 border-l-2 ${
                item.label === activeItem
                  ? 'bg-brand-primary-hover text-white border-brand-accent'
                  : 'text-slate-300 hover:bg-brand-primary-hover hover:text-white border-transparent hover:border-brand-accent'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="h-5 w-5" />
              {sidebarOpen && <span className="ml-3">{item.label}</span>}
            </Link>
          ))}
        </nav>

        <div className="p-6 border-t border-slate-700">
          {sidebarOpen && (
            <div className="mb-4 px-4">
              <p className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-xs text-slate-400" data-testid="sidebar-user-role">{user?.role?.replace('_', ' ')}</p>
              {user?.franchise_name && (
                <p className="text-xs text-brand-accent truncate">{user.franchise_name}</p>
              )}
            </div>
          )}
          
          <button
            onClick={handleLogout}
            className={`flex items-center w-full py-3 text-slate-300 hover:bg-brand-primary-hover hover:text-white transition-all duration-200
              ${sidebarOpen ? "px-4 justify-start" : "justify-center px-0"}
            `}
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
}
